import React from 'react';
import moment from 'moment';
import { Line, Bar, Pie } from 'react-chartjs-2';

const DetailedDayCard = ({ dateData, selectedDate }) => {
  if (!dateData) {
    return (
      <div className="detailed-day-card">
        <div className="no-data-message">
          <h3>📅 No Data Available</h3>
          <p>No listener data found for {moment(selectedDate).format('MMM DD, YYYY')}</p>
          <p className="hint">Try selecting a different date</p>
        </div>
      </div>
    );
  }

  // Prepare hourly chart data
  const prepareHourlyChart = () => {
    if (!dateData.hourly || dateData.hourly.length === 0) return null;

    const allHours = Array.from({ length: 24 }, (_, i) => i);
    const hourlyMap = new Map();
    
    dateData.hourly.forEach(item => {
      const hour = parseInt(item.hour);
      const listeners = parseInt(item.listeners) || parseInt(item.avg_listeners) || 0;
      if (!isNaN(hour) && hour >= 0 && hour <= 23) {
        hourlyMap.set(hour, listeners);
      }
    });

    return {
      labels: allHours.map(hour => `${hour.toString().padStart(2, '0')}:00`),
      datasets: [{
        label: 'Listeners',
        data: allHours.map(hour => hourlyMap.get(hour) || 0),
        backgroundColor: 'rgba(147, 51, 234, 0.8)',
        borderColor: 'rgb(147, 51, 234)',
        borderWidth: 2,
        tension: 0.4,
        fill: true
      }]
    };
  };

  // Prepare geographic data
  const prepareGeoChart = () => {
    if (!dateData.geographic || dateData.geographic.length === 0) return null;

    const colors = [
      'rgba(239, 68, 68, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(34, 197, 94, 0.8)',
      'rgba(59, 130, 246, 0.8)',
      'rgba(147, 51, 234, 0.8)',
      'rgba(236, 72, 153, 0.8)'
    ];

    return {
      labels: dateData.geographic.map(item => item.country),
      datasets: [{
        label: 'Listeners by Country',
        data: dateData.geographic.map(item => item.listeners),
        backgroundColor: colors.slice(0, dateData.geographic.length),
        borderWidth: 1
      }]
    };
  };

  const hourlyChart = prepareHourlyChart();
  const geoChart = prepareGeoChart();

  // Find peak hour
  const peakHour = dateData.hourly?.reduce((max, item) => 
    (item.listeners > max.listeners) ? item : max
  , { hour: 0, listeners: 0 });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: { color: '#e5e7eb' }
      }
    },
    scales: {
      x: {
        ticks: { color: '#9ca3af', maxRotation: 0, minRotation: 0 },
        grid: { color: 'rgba(156, 163, 175, 0.2)' }
      },
      y: {
        ticks: { color: '#9ca3af' },
        grid: { color: 'rgba(156, 163, 175, 0.2)' },
        beginAtZero: true
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { color: '#e5e7eb', padding: 15 }
      }
    }
  };

  return (
    <div className="detailed-day-card">
      <div className="day-card-header">
        <h2>📅 {moment(selectedDate).format('dddd, MMMM DD, YYYY')}</h2>
        <div className="quick-stats">
          <div className="quick-stat">
            <span className="stat-icon">👥</span>
            <div>
              <div className="stat-label">Peak Listeners</div>
              <div className="stat-value">{dateData.peak_listeners || 0}</div>
            </div>
          </div>
          <div className="quick-stat">
            <span className="stat-icon">🌐</span>
            <div>
              <div className="stat-label">Unique IPs</div>
              <div className="stat-value">{dateData.unique_ips || 0}</div>
            </div>
          </div>
          <div className="quick-stat">
            <span className="stat-icon">⏱️</span>
            <div>
              <div className="stat-label">Avg Duration</div>
              <div className="stat-value">
                {Math.round((dateData.avg_session_duration || 0) / 60)}m
              </div>
            </div>
          </div>
          {peakHour && peakHour.listeners > 0 && (
            <div className="quick-stat">
              <span className="stat-icon">⏰</span>
              <div>
                <div className="stat-label">Peak Hour</div>
                <div className="stat-value">
                  {String(peakHour.hour).padStart(2, '0')}:00
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="day-card-body">
        {/* Hourly Breakdown Chart */}
        {hourlyChart && (
          <div className="chart-section">
            <h3>📈 Listeners Throughout the Day</h3>
            <div className="chart-container-small">
              <Line data={hourlyChart} options={chartOptions} />
            </div>
          </div>
        )}

        {/* Geographic Distribution */}
        {geoChart && (
          <div className="chart-section">
            <h3>🌍 Geographic Distribution</h3>
            <div className="geo-container">
              <div className="chart-container-pie">
                <Pie data={geoChart} options={pieOptions} />
              </div>
              <div className="geo-list">
                <h4>Top Countries</h4>
                {dateData.geographic.slice(0, 5).map((geo, idx) => (
                  <div key={idx} className="geo-item">
                    <span className="geo-rank">#{idx + 1}</span>
                    <span className="geo-country">{geo.country}</span>
                    <span className="geo-count">{geo.listeners}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Additional Stats */}
        <div className="additional-stats">
          <div className="stat-card">
            <span className="stat-card-icon">🎯</span>
            <div className="stat-card-content">
              <div className="stat-card-label">Total Sessions</div>
              <div className="stat-card-value">{dateData.total_listeners || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-card-icon">📊</span>
            <div className="stat-card-content">
              <div className="stat-card-label">Engagement Score</div>
              <div className="stat-card-value">
                {Math.min(100, Math.round(
                  ((dateData.peak_listeners || 0) * 0.4) + 
                  ((dateData.unique_ips || 0) * 0.3) + 
                  (Math.round((dateData.avg_session_duration || 0) / 60) * 0.3)
                ))}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedDayCard;



